import React, { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReactFlagsSelect from 'react-flags-select';
import AppointmentClockPicker from '../AppointmentClockPicker';
import {
    getShopAuthState,
    getShopAvailability,
    getReusableShopReferralLink,
    saveShopHours,
    saveShopSlotConfig,
    sendShopContactChangeCodes,
    updateShopProfile,
    verifyShopContactChangeCode,
} from '../../utils/shopAuthApi';
import VerificationField, { CODE_LENGTH } from '../ShopAuth/VerificationField';
import DriverAvatar, { SHOP_GLYPH } from '../DriverAvatar';
import { ProfilePhotoActions, ProfilePhotoAvatar, SHOP_PHOTO_COPY, useProfilePhotoEditor } from '../ProfilePhotoEditor';
import { removeShopPhoto, uploadShopPhoto } from '../../utils/avatarApi';
import useUsZipLookup from '../../hooks/useUsZipLookup';
import {
    SHOP_NAME_MAX,
    SPECIALTY_MAX,
    STREET_MAX,
    getShopDetailsErrors,
    matchZipCity,
    sanitizeZip5,
    specialtyError,
    shopNameError,
} from '../../utils/usShopAddress';
import { createQrDataUrl, downloadQrPng } from '../../utils/qrCode';

// PostgreSQL EXTRACT(DOW) ordering: 0 = Sunday. Matches partner_shop_hours.weekday, so
// nothing converts between this screen and the database.
const WEEKDAYS = [
    [1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'],
    [5, 'Friday'], [6, 'Saturday'], [0, 'Sunday'],
];

const SLOT_LENGTHS = [15, 30, 45, 60, 90, 120, 180, 240];
const NOTICE_CHOICES = [
    [0, 'No minimum'], [60, '1 hour'], [120, '2 hours'],
    [240, '4 hours'], [1440, '1 day'], [2880, '2 days'],
];
const HORIZON_CHOICES = [7, 14, 30, 60, 90, 180];

// ── Contact details that have to be confirmed before they change ──────────────
// Mirrors CONTACT_FIELDS in auth/shop_router.py. A partner registers with one email and
// one phone, and both the shop record and the owner's credentials are written from it,
// so there is one of each here too: changing the shop email changes the address that
// signs you in, and the old one stops working. Both therefore need a code sent to the
// new value AND the account password.
const CONTACT_FIELDS = {
    shop_contact_email: { channel: 'email', label: 'Email address', card: 'shop' },
    shop_phone: { channel: 'sms', label: 'Phone number', card: 'shop' },
};

const digitsOf = (value) => String(value || '').replace(/\D/g, '');
const normalizedEmail = (value) => String(value || '').trim().toLowerCase();

// Is this genuinely a different address, or the same one spelled differently? Compared
// on digits for phones because normalize_phone on the server keeps a leading "+" only
// when it is sent one, and older rows really are stored without it — comparing the
// strings would demand a code for a number nobody touched.
function contactChanged(field, nextValue, currentValue) {
    if (CONTACT_FIELDS[field].channel === 'sms') {
        return digitsOf(nextValue) !== digitsOf(currentValue);
    }
    return normalizedEmail(nextValue) !== normalizedEmail(currentValue);
}

// The value as the API wants it: E.164 for phones, trimmed and lowercased for email.
function contactApiValue(field, value) {
    return CONTACT_FIELDS[field].channel === 'sms'
        ? `+${digitsOf(value)}`
        : normalizedEmail(value);
}

// The SHOP's stored value, which is what "did this change?" is measured against — the
// same rule the server uses. Measuring against the login column would make an untouched
// field look changed on an account where the two had drifted apart.
function currentContactValue(account, field) {
    const map = {
        shop_contact_email: account?.shop?.contact_email,
        shop_phone: account?.shop?.phone,
    };
    return map[field] || '';
}


function formatTime(hhmm) {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function Icon({ className = '', children }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
    );
}

const ICONS = {
    shop: <><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    qr: <><rect x="4" y="4" width="6" height="6" /><rect x="14" y="4" width="6" height="6" /><rect x="4" y="14" width="6" height="6" /><path d="M14 14h2v2h-2zM18 14h2v6h-2zM14 18h2v2h-2z" /></>,
};

const INPUT = 'h-11 w-full rounded-xl border border-[#d9d9d9] bg-white px-3.5 text-[15px] font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 disabled:bg-[#F9F9F9]';
const ROW = 'border-b border-[#f3f3f3] py-3 last:border-b-0 last:pb-0';

// react-phone-input-2 renders its own markup, so the dashboard palette has to be
// applied through its class names rather than Tailwind.
const PHONE_STYLES = `
.shop-phone { width: 100% !important; }
.shop-phone .form-control {
    width: 100% !important; height: 44px !important; padding-left: 60px !important;
    font-size: 15px !important; font-weight: 600 !important; color: #414141 !important;
    background: #fff !important; border: 1px solid #d9d9d9 !important;
    border-radius: 12px !important; font-family: inherit !important; outline: none !important;
}
.shop-phone .form-control:focus { border-color: #D81F26 !important; box-shadow: 0 0 0 4px rgba(216,31,38,.10) !important; }
.shop-phone .form-control:disabled { background: #F9F9F9 !important; }
.shop-phone .flag-dropdown { background: transparent !important; border: none !important; border-right: 1px solid #d9d9d9 !important; border-radius: 12px 0 0 12px !important; width: 50px !important; }
.shop-phone .flag-dropdown.open { background: #F2F2F2 !important; }
.shop-phone .selected-flag { width: 50px !important; padding-left: 11px !important; background: transparent !important; border-radius: 12px 0 0 12px !important; display: flex !important; align-items: center !important; gap: 6px !important; }
.shop-phone .selected-flag .flag { position: static !important; margin: 0 !important; }
.shop-phone .selected-flag .arrow { position: static !important; margin: 0 !important; border-top-color: #414141 !important; }
.shop-phone-list { margin-top: 6px !important; border: 1px solid #d9d9d9 !important; border-radius: 12px !important; box-shadow: 0 18px 40px rgba(34,34,34,.18) !important; font-family: inherit !important; max-height: 240px !important; }
.shop-phone-list .search { padding: 10px !important; border-bottom: 1px solid #f3f3f3 !important; }
.shop-phone-list .search-emoji { display: none !important; }
.shop-phone-list .search-box { width: 100% !important; margin: 0 !important; padding: 8px 11px !important; font-size: 13px !important; font-weight: 600 !important; border: 1px solid #d9d9d9 !important; border-radius: 9px !important; outline: none !important; font-family: inherit !important; }
.shop-phone-list .country { padding: 8px 12px !important; display: flex !important; align-items: center !important; gap: 8px !important; }
.shop-phone-list .country .country-name { font-size: 13px !important; font-weight: 600 !important; color: #414141 !important; }
.shop-phone-list .country .dial-code { font-size: 12px !important; font-weight: 700 !important; color: #414141 !important; }
.shop-phone-list .country:hover, .shop-phone-list .country.highlight { background: #F2F2F2 !important; }

.shop-country button[class*="ReactFlagsSelect-module_selectBtn"] {
    height: 44px; border: 1px solid #d9d9d9; border-radius: 12px; background: #fff;
    font-size: 15px; font-weight: 600; color: #414141; padding: 0 12px;
}
.shop-country div[class*="ReactFlagsSelect-module_selectOptions"] {
    border: 1px solid #d9d9d9; border-radius: 12px; box-shadow: 0 18px 40px rgba(34,34,34,.18);
    max-height: 240px; margin-top: 6px;
}
.shop-country input[class*="ReactFlagsSelect-module_filterText"] {
    border: 1px solid #d9d9d9; border-radius: 9px; padding: 8px 11px;
    font-size: 13px; font-weight: 600; font-family: inherit; outline: none; width: 100%;
}
.shop-country span[class*="ReactFlagsSelect-module_label"] { font-size: 15px; font-weight: 600; }
.shop-country ul[class*="ReactFlagsSelect-module_selectOptions"] li { font-size: 14px; }
`;

// Country names come from the browser's own ICU data, so there is no list to maintain
// and it localises for free. The codes themselves come from the API, which sources
// them from the library that provides the holidays.
const REGION_NAMES = (() => {
    try {
        return new Intl.DisplayNames(undefined, { type: 'region' });
    } catch {
        return null;
    }
})();

function countryName(code) {
    if (!code) return '';
    try {
        return REGION_NAMES?.of(code) || code;
    } catch {
        return code;
    }
}


// Consecutive days sharing the same hours collapse into one line ("Mon – Fri"), which
// is how opening hours are shown everywhere people already read them.
function summariseHours(hours) {
    const byDay = WEEKDAYS.map(([value, label]) => {
        // A day can hold more than one window — a break splits it into two (e.g.
        // 9–1 and 2–5). Show every window so a lunch break reads back correctly.
        const dayWindows = (hours || [])
            .filter((x) => x.weekday === value)
            .sort((a, b) => (a.opens_at > b.opens_at ? 1 : -1));
        const text = dayWindows.length
            ? dayWindows.map((w) => `${formatTime(w.opens_at)} – ${formatTime(w.closes_at)}`).join(', ')
            : null;
        return { label, text };
    });
    const groups = [];
    byDay.forEach((day) => {
        const last = groups[groups.length - 1];
        if (last && last.text === day.text) last.end = day.label;
        else groups.push({ start: day.label, end: null, text: day.text });
    });
    return groups.map((g) => ({
        days: g.end ? `${g.start.slice(0, 3)} – ${g.end.slice(0, 3)}` : g.start,
        text: g.text,
    }));
}

function SectionCard({
    icon, title, description, isEditing, onEdit, onCancel, isSaving, canEdit,
    saveLabel = 'Save', saveDisabled = false, children,
}) {
    return (
        <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F2F2F2] text-[#414141] sm:h-11 sm:w-11 sm:rounded-2xl">
                        <Icon className="h-5 w-5">{icon}</Icon>
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-black text-[#414141]">{title}</h2>
                        {description ? <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">{description}</p> : null}
                    </div>
                </div>
                {canEdit ? (
                    <div className="flex shrink-0 gap-2">
                        {isEditing ? (
                            <>
                                <button type="button" onClick={onCancel} disabled={isSaving}
                                    className="inline-flex min-h-[44px] items-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-60">
                                    Cancel
                                </button>
                                <button type="submit" disabled={isSaving || saveDisabled}
                                    className="inline-flex min-h-[44px] items-center rounded-full bg-[#D81F26] px-5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:opacity-60">
                                    {isSaving ? 'Saving...' : saveLabel}
                                </button>
                            </>
                        ) : (
                            <button type="button" onClick={onEdit}
                                className="inline-flex min-h-[44px] items-center rounded-full border border-[#D9D9D9] bg-white px-5 text-sm font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]">
                                Edit
                            </button>
                        )}
                    </div>
                ) : null}
            </div>
            <div className="mt-4">{children}</div>
        </section>
    );
}

function ReadRow({ label, value, locked, note, pill }) {
    // pill renders the value as a chip: accent when configured, amber when it still says
    // "Not set", so an unfinished setting is visible without reading every line.
    const isUnset = !value || value === 'Not set' || value === 'Closed';
    return (
        <div className={ROW}>
            <div className="flex items-center justify-between gap-4">
                <span className="text-[13px] font-bold text-[#414141]">{label}</span>
                <span className="flex min-w-0 items-center gap-1.5">
                    {locked ? <Icon className="h-3.5 w-3.5 shrink-0 text-[#aeaeae]">{ICONS.lock}</Icon> : null}
                    {pill ? (
                        <span className={`truncate rounded-full px-2.5 py-1 text-[13px] font-black ${isUnset
                            ? 'bg-[#fdf3e4] text-[#9a5407]' : 'bg-[#F2F2F2] text-[#414141]'}`}>
                            {value || 'Not set'}
                        </span>
                    ) : (
                        <span className="truncate text-[15px] font-black text-[#414141]">{value || 'Not set'}</span>
                    )}
                </span>
            </div>
            {note ? <p className="mt-1 text-xs font-medium leading-relaxed text-[#414141]">{note}</p> : null}
        </div>
    );
}

function EditRow({ id, label, note, bare, ...props }) {
    // bare drops the row divider so two fields can sit side by side inside one Pair.
    return (
        <label className={`block ${bare ? '' : ROW}`} htmlFor={id}>
            <span className="text-[13px] font-bold text-[#414141]">{label}</span>
            <input id={id} className={`mt-1.5 ${INPUT}`} {...props} />
            {note ? <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#414141]">{note}</p> : null}
        </label>
    );
}

// Short fields share a line so the form stays a screenful instead of a column of
// full-width boxes.
function Pair({ children }) {
    return <div className={`grid grid-cols-2 gap-3 ${ROW}`}>{children}</div>;
}

function SelectRow({ id, label, note, options, bare, ...props }) {
    return (
        <label className={`block ${bare ? '' : ROW}`} htmlFor={id}>
            <span className="text-[13px] font-bold text-[#414141]">{label}</span>
            {/* "Not set" is a real answer: it clears the setting rather than storing a
                number the shop never chose. */}
            <select id={id} className={`mt-1.5 ${INPUT}`} {...props}>
                <option value="">Not set</option>
                {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
            {note ? <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#414141]">{note}</p> : null}
        </label>
    );
}

// One line per day: a toggle and, when open, the two times — plus an optional break
// (e.g. lunch 1–2 PM) that carves an unavailable gap out of the day. Reuses the
// driver-side clock picker rather than introducing a second style of time input.
function DayRow({ label, window, disabled, onChange }) {
    const hasBreak = Boolean(window && window.break_start && window.break_end);
    return (
        <div className={ROW}>
            <div className="flex flex-wrap items-center gap-2.5">
                <span className="w-[76px] shrink-0 text-[13px] font-bold text-[#414141]">{label}</span>
                <button type="button" disabled={disabled}
                    onClick={() => onChange(window ? null : { opens_at: '09:00', closes_at: '17:00' })}
                    className={`w-[74px] shrink-0 rounded-full px-3 py-1.5 text-xs font-black transition ${window
                        ? 'bg-[#F9F9F9] text-[#414141]' : 'bg-[#F9F9F9] text-[#414141]'} disabled:opacity-50`}>
                    {window ? 'Open' : 'Closed'}
                </button>
                {window ? (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <AppointmentClockPicker value={window.opens_at}
                                onChange={(v) => onChange({ ...window, opens_at: v })} />
                        </div>
                        <span className="text-xs font-bold text-[#414141]">to</span>
                        <div className="min-w-0 flex-1">
                            <AppointmentClockPicker value={window.closes_at}
                                onChange={(v) => onChange({ ...window, closes_at: v })} />
                        </div>
                    </div>
                ) : null}
            </div>
            {window ? (
                <div className="mt-2 flex flex-wrap items-center gap-2.5 sm:pl-[86px]">
                    <button type="button" disabled={disabled}
                        onClick={() => onChange(hasBreak
                            ? { opens_at: window.opens_at, closes_at: window.closes_at }
                            : { ...window, break_start: '13:00', break_end: '14:00' })}
                        className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-black transition ${hasBreak
                            ? 'bg-[#fdeae1] text-[#b4501f]' : 'bg-[#F9F9F9] text-[#414141]'} disabled:opacity-50`}>
                        {hasBreak ? 'Remove break' : '+ Add break'}
                    </button>
                    {hasBreak ? (
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="shrink-0 text-[11px] font-black uppercase tracking-wide text-[#b4501f]">Break</span>
                            <div className="min-w-0 flex-1">
                                <AppointmentClockPicker value={window.break_start}
                                    onChange={(v) => onChange({ ...window, break_start: v })} />
                            </div>
                            <span className="text-xs font-bold text-[#414141]">to</span>
                            <div className="min-w-0 flex-1">
                                <AppointmentClockPicker value={window.break_end}
                                    onChange={(v) => onChange({ ...window, break_end: v })} />
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}


export default function ShopSettingsPage() {
    const [account, setAccount] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [status, setStatus] = useState('loading');
    const [loadError, setLoadError] = useState('');
    // One card open at a time, so a half-finished edit is never silently discarded.
    const [editing, setEditing] = useState('');
    const [draft, setDraft] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedCard, setSavedCard] = useState('');
    const [reusableReferral, setReusableReferral] = useState(null);
    const [reusableReferralCopied, setReusableReferralCopied] = useState(false);
    const [reusableReferralError, setReusableReferralError] = useState('');
    // Set once Save has been pressed on a card whose contact details changed. Holds the
    // pending new values, one code box per field, and — for a sign-in identifier — the
    // password prompt. Null the rest of the time, which is the ordinary "just save it"
    // path for a card where only the address or the name moved.
    const [verify, setVerify] = useState(null);

    const reloadAvailability = useCallback(
        () => getShopAvailability().then(setAvailability).catch(() => {}), []);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            getShopAuthState(),
            getShopAvailability().catch(() => null),
            getReusableShopReferralLink().catch((err) => ({ error: err?.message || 'Could not load your reusable referral link.' })),
        ])
            .then(([data, avail, reusable]) => {
                if (cancelled) return;
                if (!data?.authenticated) {
                    setStatus('error');
                    setLoadError('Your session has expired. Sign in again to manage settings.');
                    return;
                }
                setAccount(data);
                setAvailability(avail);
                if (reusable?.error) setReusableReferralError(reusable.error);
                else setReusableReferral(reusable);
                setStatus('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                setStatus('error');
                setLoadError(err?.message || 'Could not load your shop profile.');
            });
        return () => { cancelled = true; };
    }, []);

    // Deep-link: /settings#availability (from the calendar's "Edit business hours") scrolls
    // straight to the Availability card once it's rendered, instead of the top of the page.
    useEffect(() => {
        if (status !== 'ready') return undefined;
        if (typeof window === 'undefined' || window.location.hash !== '#availability') return undefined;
        const t = window.setTimeout(() => {
            document.getElementById('availability')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 120);
        return () => window.clearTimeout(t);
    }, [status]);

    const user = account?.partner_user;
    const shop = account?.shop;
    const canEdit = ['owner', 'manager'].includes(account?.membership_role);

    // Shop photo: the same control as the driver's profile picture. PartnerShell owns the
    // URL so the sidebar updates too; fall back to this page's own copy outside the shell.
    const outlet = useOutletContext() || {};
    const [localPhotoUrl, setLocalPhotoUrl] = useState('');
    const shopPhotoUrl = (outlet.setShopPhotoUrl ? outlet.shopPhotoUrl : localPhotoUrl) ?? (shop?.photo_url || '');
    const setShopPhotoUrl = outlet.setShopPhotoUrl || setLocalPhotoUrl;
    useEffect(() => {
        if (!outlet.setShopPhotoUrl && shop?.photo_url) setLocalPhotoUrl(shop.photo_url);
    }, [outlet.setShopPhotoUrl, shop?.photo_url]);
    // Shop details edits use the signup rules: the ZIP fills City and State and must
    // exist, and the city and state must belong to it.
    const zipLookup = useUsZipLookup(editing === 'shop' ? draft.postal_code || '' : '');
    useEffect(() => {
        if (editing !== 'shop') return;
        if (zipLookup.status === 'found') {
            const { info } = zipLookup;
            setDraft((d) => ({ ...d, region: info.state, city: matchZipCity(d.city, info) || info.cities[0] || '' }));
        } else if (zipLookup.status === 'idle' || zipLookup.status === 'not_found') {
            setDraft((d) => (d.region ? { ...d, region: '' } : d));
        }
    }, [zipLookup, editing]);

    const photoEditor = useProfilePhotoEditor({
        avatarUrl: shopPhotoUrl,
        onAvatarChange: setShopPhotoUrl,
        upload: uploadShopPhoto,
        remove: removeShopPhoto,
        copy: SHOP_PHOTO_COPY,
    });

    const openEditor = (card, initial) => {
        setEditing(card); setDraft(initial); setError(''); setSavedCard(''); setVerify(null);
    };
    const closeEditor = () => { setEditing(''); setDraft({}); setError(''); setVerify(null); };
    const setField = (key) => (e) => { setDraft((d) => ({ ...d, [key]: e.target.value })); setError(''); };

    const run = async (card, action) => {
        setError('');
        setIsSaving(true);
        try {
            await action();
            setEditing('');
            setDraft({});
            setVerify(null);
            setSavedCard(card);
        } catch (err) {
            setError(err?.message || 'Could not save your changes.');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Contact confirmation ──────────────────────────────────────────────────

    // Which of this card's contact details actually differ from what is stored. An
    // address-only edit returns nothing here and saves without ever asking for a code.
    const pendingContactChanges = useCallback((card) => {
        const changed = {};
        Object.entries(CONTACT_FIELDS).forEach(([field, meta]) => {
            if (meta.card !== card) return;
            const next = draft[field];
            if (next === undefined || next === null || next === '') return;
            if (contactChanged(field, next, currentContactValue(account, field))) {
                changed[field] = contactApiValue(field, next);
            }
        });
        return changed;
    }, [draft, account]);

    const startCooldown = useCallback((field, seconds) => {
        setVerify((v) => (v ? {
            ...v,
            fields: { ...v.fields, [field]: { ...v.fields[field], cooldown: Math.max(0, Math.ceil(seconds || 0)) } },
        } : v));
    }, []);

    // One ticker for every countdown on screen, rather than one timer per field.
    useEffect(() => {
        if (!verify) return undefined;
        if (!Object.values(verify.fields).some((f) => f.cooldown > 0)) return undefined;
        const id = window.setInterval(() => {
            setVerify((v) => {
                if (!v) return v;
                const fields = {};
                Object.entries(v.fields).forEach(([field, state]) => {
                    fields[field] = { ...state, cooldown: Math.max(0, (state.cooldown || 0) - 1) };
                });
                return { ...v, fields };
            });
        }, 1000);
        return () => window.clearInterval(id);
    }, [verify]);

    // Turns the server's per-field send report into the code boxes on screen. Reused by
    // the first send and by every resend, so a resend cannot drift out of step with it.
    const applySendResult = useCallback((data, previous) => {
        const fields = {};
        Object.entries(data?.fields || {}).forEach(([field, info]) => {
            const before = previous?.[field] || {};
            fields[field] = {
                channel: info.channel,
                sentTo: info.sent_to || before.sentTo || '',
                code: before.code || '',
                // A resend replaces the code, so a tick earned by the previous one is no
                // longer meaningful and has to be earned again.
                verified: false,
                checking: false,
                status: info.status === 'failed'
                    ? 'That code could not be sent. Try again.'
                    : '',
                cooldown: info.status === 'throttled'
                    ? Math.ceil(info.retry_after || 0)
                    : Math.ceil(data?.resend_after || 60),
            };
        });
        return fields;
    }, []);

    // Save on a card whose contact details changed: ask for the codes, then show the
    // boxes. Nothing is written until confirmContactChange below.
    const beginContactVerification = async (card, changes) => {
        setError('');
        setIsSaving(true);
        try {
            const data = await sendShopContactChangeCodes({
                shopContactEmail: changes.shop_contact_email,
                shopPhone: changes.shop_phone,
            });
            const fields = applySendResult(data, null);
            if (!Object.keys(fields).length) {
                // The server decided nothing really changed after normalizing — a phone
                // retyped in a different format, say. Fall through to a plain save.
                return { confirmed: true };
            }
            // Always: both fields are credentials now, so every change is an identity
            // change and the password is the "is it really you" check.
            setVerify({ card, changes, fields, needsPassword: true, password: '' });
            return { confirmed: false };
        } catch (err) {
            setError(err?.message || 'Could not send the confirmation codes.');
            return { confirmed: false, failed: true };
        } finally {
            setIsSaving(false);
        }
    };

    const setCode = (field) => async (value) => {
        setVerify((v) => (v ? {
            ...v,
            fields: { ...v.fields, [field]: { ...v.fields[field], code: value, status: '' } },
        } : v));
        if (value.length !== CODE_LENGTH) return;
        // Check as soon as the sixth digit lands, so the owner finds out now rather than
        // at Save. consume=False server-side, so the code survives for the save itself.
        setVerify((v) => (v ? {
            ...v, fields: { ...v.fields, [field]: { ...v.fields[field], checking: true } },
        } : v));
        try {
            const state = verify?.fields?.[field];
            await verifyShopContactChangeCode({
                identifier: verify.changes[field],
                channel: state?.channel || CONTACT_FIELDS[field].channel,
                otp: value,
            });
            setVerify((v) => (v ? {
                ...v,
                fields: { ...v.fields, [field]: { ...v.fields[field], verified: true, checking: false, status: '' } },
            } : v));
        } catch (err) {
            setVerify((v) => (v ? {
                ...v,
                fields: {
                    ...v.fields,
                    [field]: {
                        ...v.fields[field],
                        verified: false,
                        checking: false,
                        status: err?.message || 'That code is not right.',
                    },
                },
            } : v));
        }
    };

    const resendCode = (field) => async () => {
        setError('');
        try {
            const data = await sendShopContactChangeCodes({
                shopContactEmail: verify.changes.shop_contact_email,
                shopPhone: verify.changes.shop_phone,
                onlyField: field,
            });
            setVerify((v) => (v ? { ...v, fields: { ...v.fields, ...applySendResult(data, v.fields) } } : v));
        } catch (err) {
            // 429 carries the server's own Retry-After, so the countdown on screen always
            // matches the cooldown the server will actually enforce.
            if (err?.retryAfterSeconds) startCooldown(field, err.retryAfterSeconds);
            setVerify((v) => (v ? {
                ...v,
                fields: { ...v.fields, [field]: { ...v.fields[field], status: err?.message || 'Could not resend.' } },
            } : v));
        }
    };

    // Everything the confirmed changes add to a profile save: the new values, one code
    // each, and the password when a sign-in identifier is moving.
    const contactPayload = () => {
        if (!verify) return {};
        const payload = {};
        Object.entries(verify.changes).forEach(([field, value]) => {
            payload[field] = value;
            payload[`${field}_otp`] = verify.fields[field]?.code || '';
        });
        if (verify.needsPassword) payload.current_password = verify.password;
        return payload;
    };

    const allCodesVerified = verify
        ? Object.values(verify.fields).every((f) => f.verified)
        : true;
    const canConfirm = allCodesVerified && (!verify?.needsPassword || Boolean(verify.password));

    const saveProfile = async (basePayload) => {
        setAccount(await updateShopProfile({ ...basePayload, ...contactPayload() }));
        await reloadAvailability();   // country may have changed, so holidays may too
    };

    // PhoneInput emits bare digits; the API expects E.164, same as signup sends.
    const shopBasePayload = () => {
        const payload = { ...draft };
        if (payload.shop_phone) payload.shop_phone = `+${digitsOf(payload.shop_phone)}`;
        return payload;
    };

    // Name and specialty are always checked; the address only when it was changed, so an
    // address saved before these rules doesn't block renaming the shop.
    const shopDetailsError = () => {
        const nameError = shopNameError(draft.shop_name);
        if (nameError) return nameError;
        const specialtyMessage = specialtyError(draft.specialty);
        if (specialtyMessage) return specialtyMessage;
        const addressKeys = ['street_address', 'city', 'region', 'postal_code', 'country_code'];
        const squash = (v) => String(v || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const addressChanged = addressKeys.some((key) => squash(draft[key]) !== squash(shop?.[key]));
        if (!addressChanged) return '';
        const errors = getShopDetailsErrors({
            shopName: draft.shop_name,
            streetAddress: draft.street_address,
            city: draft.city,
            region: draft.region,
            postalCode: draft.postal_code,
            countryCode: draft.country_code,
            specialty: draft.specialty,
        }, zipLookup);
        const order = ['countryCode', 'streetAddress', 'postalCode', 'city', 'region'];
        const field = order.find((key) => errors[key]);
        return field ? errors[field] : '';
    };

    const submitProfile = async (e) => {
        e.preventDefault();
        const detailsError = shopDetailsError();
        if (detailsError) {
            setError(detailsError);
            return undefined;
        }
        if (verify?.card === 'shop') {
            return run('shop', () => saveProfile(shopBasePayload()));
        }
        const changes = pendingContactChanges('shop');
        if (Object.keys(changes).length) {
            const { confirmed } = await beginContactVerification('shop', changes);
            if (!confirmed) return undefined;
        }
        return run('shop', () => saveProfile(shopBasePayload()));
    };

    // Your account only carries the owner's name now — the email and phone that sign
    // them in are the shop's, and are edited in Shop details.
    const submitAccount = (e) => {
        e.preventDefault();
        return run('account', () => saveProfile({ owner_name: draft.owner_name }));
    };

    const submitAvailability = (e) => {
        e.preventDefault();
        return run('availability', async () => {
            const windows = [];
            Object.entries(draft.byDay || {}).forEach(([weekday, w]) => {
                if (!w?.opens_at || !w?.closes_at) return;
                const wd = Number(weekday);
                // A valid break (inside the open window, start before end) carves the day
                // into two windows the slot generator naturally skips the gap between.
                // Anything malformed falls back to one continuous window rather than
                // sending overlapping/inverted rows the backend would reject.
                const validBreak = w.break_start && w.break_end
                    && w.break_start > w.opens_at && w.break_end < w.closes_at
                    && w.break_start < w.break_end;
                if (validBreak) {
                    windows.push({ weekday: wd, opens_at: w.opens_at, closes_at: w.break_start });
                    windows.push({ weekday: wd, opens_at: w.break_end, closes_at: w.closes_at });
                } else {
                    windows.push({ weekday: wd, opens_at: w.opens_at, closes_at: w.closes_at });
                }
            });
            await saveShopHours(windows);
            const asNumber = (v) => (v === '' || v === undefined ? null : Number(v));
            await saveShopSlotConfig({
                slot_minutes: asNumber(draft.slot_minutes),
                concurrent_capacity: asNumber(draft.concurrent_capacity),
                min_notice_minutes: asNumber(draft.min_notice_minutes),
                max_advance_days: asNumber(draft.max_advance_days),
            });
            await reloadAvailability();
        });
    };

    const reusableReferralUrl = reusableReferral?.url || '';
    const reusableReferralQr = reusableReferralUrl ? createQrDataUrl(reusableReferralUrl, { scale: 5, border: 4 }) : '';

    const copyReusableReferralUrl = async () => {
        if (!reusableReferralUrl) return;
        await navigator.clipboard.writeText(reusableReferralUrl);
        setReusableReferralCopied(true);
        window.setTimeout(() => setReusableReferralCopied(false), 1600);
    };

    const downloadReusableReferralQr = async () => {
        if (!reusableReferralUrl) return;
        const shopSlug = String(shop?.name || 'shop').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'shop';
        await downloadQrPng(reusableReferralUrl, `carfido-${shopSlug}-referral-qr.png`);
    };

    const errorBox = error ? (
        <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-bold text-red-700">{error}</p>
    ) : null;

    // Replaces the card's fields once Save has been pressed on a changed contact detail.
    // The edited values are held in `draft` and nothing has been written, so Cancel puts
    // the owner back where they were rather than losing the edit.
    const renderVerifyPanel = (card) => {
        if (verify?.card !== card) return null;
        return (
            <>
                <p className="mb-3 text-sm font-medium leading-relaxed text-[#414141]">
                    {Object.keys(verify.fields).length > 1
                        ? 'Enter the codes we just sent, so we know these new details reach you.'
                        : 'Enter the code we just sent, so we know this new detail reaches you.'}
                </p>
                <div className="space-y-3">
                    {Object.entries(verify.fields).map(([field, state]) => (
                        <VerificationField
                            key={field}
                            tone="dashboard"
                            id={`verify-${field}`}
                            label={CONTACT_FIELDS[field].label}
                            sentTo={state.sentTo}
                            value={state.code}
                            onChange={setCode(field)}
                            verified={state.verified}
                            checking={state.checking}
                            cooldown={state.cooldown}
                            onResend={resendCode(field)}
                            disabled={isSaving}
                            status={state.status}
                        />
                    ))}
                </div>
                {verify.needsPassword ? (
                    <label className="mt-3 block" htmlFor="verify-password">
                        <span className="text-[13px] font-bold text-[#414141]">Your current password</span>
                        <input
                            id="verify-password"
                            type="password"
                            autoComplete="current-password"
                            value={verify.password}
                            disabled={isSaving}
                            onChange={(e) => {
                                const password = e.target.value;
                                setVerify((v) => (v ? { ...v, password } : v));
                                setError('');
                            }}
                            className={`mt-1.5 ${INPUT}`}
                        />
                        <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#414141]">
                            You are changing how you sign in, so we check it is really you.{' '}
                            {/* Without this the panel is a dead end for anyone who has
                                forgotten their password. The reset flow already handles
                                it — it just was not reachable from here. Note that a
                                reset revokes every session, so they will land back on
                                the login page and have to sign in again. */}
                            {/* ?from=settings tells that page not to bounce a signed-in
                                visitor back to the dashboard, which is its normal and
                                correct behaviour when it is reached from the login screen. */}
                            <Link to="/forgot-password?from=settings"
                                className="font-bold text-[#D81F26] underline underline-offset-2 transition hover:text-[#B8181E]">
                                Forgot your password?
                            </Link>
                        </p>
                    </label>
                ) : null}
                {errorBox}
            </>
        );
    };

    if (status === 'loading') {
        return (
            <div className="flex h-full w-full items-center justify-center bg-[#F9F9F9]">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#d9d9d9] border-t-[#D81F26]" />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar">
                <div className="mx-auto w-full max-w-3xl px-4 pt-8 md:px-6 md:pt-10">
                    <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{loadError}</p>
                </div>
            </div>
        );
    }

    const config = availability?.slot_config || {};
    // Unset does not mean unbookable: shop_availability falls back to weekdays 9–5 in
    // 30-minute slots, and drivers are offered those times. The owner has to be told
    // exactly what is being published on their behalf.
    const hoursUnset = !availability?.hours?.length;
    const slotUnset = !config.slot_minutes;

    return (
        <div className="relative h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar">
            <style>{PHONE_STYLES}</style>
            <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col px-4 pb-12 pt-8 md:px-6 md:pt-10">
                <header className="mb-5">
                    <h1 className="text-3xl font-black tracking-tight text-[#414141] md:text-[34px]">Settings</h1>
                    <p className="mt-2 max-w-xl text-[15px] font-medium leading-relaxed text-[#414141]">
                        Your shop profile, when you are open, and your account.
                    </p>
                </header>

                <div className="space-y-4">
                    <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                        <div className="flex min-w-0 items-center gap-4">
                            {canEdit ? (
                                <ProfilePhotoAvatar editor={photoEditor} />
                            ) : (
                                <DriverAvatar src={shopPhotoUrl} alt="Shop photo" glyph={SHOP_GLYPH} className="h-16 w-16" iconSize={24} strokeWidth={2.3} />
                            )}
                            <div className="min-w-0">
                                <p className="truncate text-xl font-black leading-tight text-[#414141]">{shop?.name || 'Your shop'}</p>
                                <p className="mt-0.5 truncate text-sm font-medium text-[#414141]">{user?.name || ''}</p>
                                <span className="mt-2 inline-flex items-center rounded-full bg-[#F9F9F9] px-2.5 py-1 text-[11px] font-black capitalize text-[#414141]">
                                    {account?.membership_role}
                                </span>
                                <div><ProfilePhotoActions editor={photoEditor} /></div>
                            </div>
                        </div>
                    </section>

                    {/* ── Shop details ─────────────────────────────────────────── */}
                    <form onSubmit={submitProfile}>
                        <SectionCard icon={ICONS.shop} title="Shop details"
                            description={canEdit ? 'What drivers see, and where leads are sent.' : 'Only an owner or manager can change these.'}
                            canEdit={canEdit} isEditing={editing === 'shop'} isSaving={isSaving}
                            saveLabel={verify?.card === 'shop' ? 'Confirm & save' : 'Save'}
                            saveDisabled={verify?.card === 'shop' && !canConfirm}
                            onEdit={() => openEditor('shop', {
                                shop_name: shop?.name || '', specialty: shop?.specialty || '',
                                street_address: shop?.street_address || '', city: shop?.city || '',
                                region: shop?.region || '', postal_code: shop?.postal_code || '',
                                country_code: shop?.country_code || '',
                                is_mobile_mechanic: Boolean(shop?.is_mobile_mechanic),
                                shop_phone: (shop?.phone || '').replace(/\D/g, ''),
                                shop_contact_email: shop?.contact_email || '',
                            })}
                            onCancel={closeEditor}>
                            {verify?.card === 'shop' ? renderVerifyPanel('shop') : editing === 'shop' ? (
                                <>
                                    <Pair>
                                        <EditRow bare id="s-name" label="Shop name" value={draft.shop_name} onChange={setField('shop_name')} disabled={isSaving} maxLength={SHOP_NAME_MAX} required />
                                        <EditRow bare id="s-spec" label="Primary specialty" value={draft.specialty} onChange={setField('specialty')} disabled={isSaving} maxLength={SPECIALTY_MAX} required />
                                    </Pair>
                                    <label htmlFor="s-mobile-mechanic" className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                                        <input
                                            id="s-mobile-mechanic"
                                            type="checkbox"
                                            checked={Boolean(draft.is_mobile_mechanic)}
                                            onChange={(event) => { setDraft((d) => ({ ...d, is_mobile_mechanic: event.target.checked })); setError(''); }}
                                            disabled={isSaving}
                                            className="mt-0.5 h-4 w-4 accent-[#D81F26]"
                                        />
                                        <span>
                                            <span className="block text-[13px] font-black text-[#414141]">Mobile mechanic</span>
                                            <span className="mt-0.5 block text-xs font-medium leading-relaxed text-[#414141]">Show drivers that your team can travel to the vehicle.</span>
                                        </span>
                                    </label>
                                    <EditRow id="s-street" label="Street address" value={draft.street_address} onChange={setField('street_address')} disabled={isSaving} maxLength={STREET_MAX} required />
                                    <Pair>
                                        <EditRow bare id="s-city" label="City" value={draft.city} onChange={setField('city')} disabled={isSaving}
                                            list={zipLookup.status === 'found' && zipLookup.info.cities.length > 1 ? 's-city-options' : undefined} required />
                                        {zipLookup.status === 'found' && zipLookup.info.cities.length > 1 && (
                                            <datalist id="s-city-options">
                                                {zipLookup.info.cities.map((name) => <option key={name} value={name} />)}
                                            </datalist>
                                        )}
                                        {/* Set from the ZIP code, so it can't disagree with it. */}
                                        <EditRow bare id="s-region" label="State / region" value={draft.region} onChange={setField('region')} disabled={isSaving}
                                            readOnly={zipLookup.status === 'found'} />
                                    </Pair>
                                    <Pair>
                                        <EditRow bare id="s-postal" label="Postal code" value={draft.postal_code} disabled={isSaving}
                                            inputMode="numeric" pattern="[0-9]*" maxLength={5}
                                            onChange={(e) => { setDraft((d) => ({ ...d, postal_code: sanitizeZip5(e.target.value) })); setError(''); }}
                                            note={zipLookup.status === 'checking' ? 'Checking the ZIP code...' : undefined} />
                                        <div>
                                            <span className="text-[13px] font-bold text-[#414141]">Country</span>
                                            <div className="mt-1.5 shop-country">
                                                {/* The library carries the full ISO country list with flags and a
                                                    search box, so there is no list to serve or keep sorted here. */}
                                                <ReactFlagsSelect
                                                    selected={draft.country_code || ''}
                                                    onSelect={(code) => { setDraft((d) => ({ ...d, country_code: code })); setError(''); }}
                                                    searchable
                                                    searchPlaceholder="Search countries"
                                                    placeholder="Select country"
                                                    disabled={isSaving}
                                                />
                                            </div>
                                            <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#414141]">
                                                Your shop's country, used for local date and time formatting.
                                            </p>
                                        </div>
                                    </Pair>
                                    <EditRow id="s-email" label="Email address" type="email" value={draft.shop_contact_email} onChange={setField('shop_contact_email')} disabled={isSaving}
                                        note="Where driver leads are sent, and the address you sign in with. Changing it sends a code to the new address; the old one stops signing you in." />
                                    <div className={ROW}>
                                        <span className="text-[13px] font-bold text-[#414141]">Phone number</span>
                                        <div className="mt-1.5">
                                            <PhoneInput
                                                country={(draft.country_code || 'us').toLowerCase()}
                                                value={draft.shop_phone}
                                                onChange={(value) => {
                                                    // Deliberately does not touch country_code: a shop can trade in one
                                                    // country on a number registered in another, so the two are set
                                                    // independently.
                                                    setDraft((d) => ({ ...d, shop_phone: String(value || '').replace(/\D/g, '') }));
                                                    setError('');
                                                }}
                                                enableSearch
                                                searchPlaceholder="Search country or code…"
                                                disableSearchIcon
                                                countryCodeEditable={false}
                                                disabled={isSaving}
                                                containerClass="shop-phone"
                                                dropdownClass="shop-phone-list"
                                                inputProps={{ id: 's-phone', autoComplete: 'tel' }}
                                            />
                                        </div>
                                        <p className="mt-1.5 text-xs font-medium leading-relaxed text-[#414141]">
                                            Shown to drivers, and signs you in. Changing it sends a code to
                                            the new number; the old one stops signing you in.
                                        </p>
                                    </div>
                                    {errorBox}
                                </>
                            ) : (
                                <>
                                    <ReadRow label="Shop name" value={shop?.name} />
                                    <ReadRow label="Primary specialty" value={shop?.specialty} />
                                    <ReadRow label="Mobile mechanic" value={shop?.is_mobile_mechanic ? 'Yes — we come to the vehicle' : 'No'} />
                                    <ReadRow label="Address" value={shop?.address} />
                                    <ReadRow label="Country" value={countryName(shop?.country_code)} />
                                    <ReadRow label="Phone number" value={shop?.phone} />
                                    <ReadRow label="Email address" value={shop?.contact_email} />
                                    {savedCard === 'shop' ? <p className="mt-3 text-sm font-bold text-[#087A43]">Saved.</p> : null}
                                </>
                            )}
                        </SectionCard>
                    </form>

                    <SectionCard icon={ICONS.qr} title="Reusable referral QR"
                        description="For your front desk, website, printed material, and voicemail."
                        canEdit={false}>
                        {reusableReferralUrl ? (
                            <div className="grid gap-4 md:grid-cols-[180px_1fr] md:items-start">
                                <div className="flex justify-center rounded-2xl border border-[#d9d9d9] bg-white p-4">
                                    <img
                                        src={reusableReferralQr}
                                        alt="Reusable shop referral QR code"
                                        className="h-36 w-36"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[13px] font-bold text-[#414141]">Plain URL</p>
                                    <div className="mt-1.5 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2">
                                        <p className="break-all text-sm font-black text-[#414141]">{reusableReferralUrl}</p>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={copyReusableReferralUrl}
                                            className="inline-flex min-h-[40px] items-center rounded-full border border-[#D9D9D9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2]"
                                        >
                                            {reusableReferralCopied ? 'Copied' : 'Copy URL'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={downloadReusableReferralQr}
                                            className="inline-flex min-h-[40px] items-center rounded-full bg-[#D81F26] px-4 text-sm font-black text-white transition hover:bg-[#B8181E]"
                                        >
                                            Download QR PNG
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs font-semibold leading-relaxed text-[#414141]">
                                        This code is permanent for this shop. Customers scan it, verify, and start a shop-locked CarFido intake.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <p className="rounded-xl border border-[#f7d58f] bg-[#fff8e8] px-4 py-3 text-sm font-bold text-[#936000]">
                                {reusableReferralError || 'Reusable referral link is loading.'}
                            </p>
                        )}
                    </SectionCard>

                    {/* ── Availability: hours and slots in one card ─────────────── */}
                    <form id="availability" onSubmit={submitAvailability} className="scroll-mt-6">
                        <SectionCard icon={ICONS.clock} title="Availability"
                            description="When you are open, and how those hours are divided into bookable slots."
                            canEdit={canEdit} isEditing={editing === 'availability'} isSaving={isSaving}
                            onEdit={() => {
                                const byDay = {};
                                WEEKDAYS.forEach(([value]) => {
                                    // Rebuild each day from its windows. Two windows means a
                                    // break: open = first.open, close = last.close, and the gap
                                    // between them (first.close → last.open) is the break.
                                    const dayWindows = (availability?.hours || [])
                                        .filter((w) => w.weekday === value)
                                        .sort((a, b) => (a.opens_at > b.opens_at ? 1 : -1));
                                    if (!dayWindows.length) {
                                        byDay[value] = null;
                                    } else if (dayWindows.length === 1) {
                                        byDay[value] = { opens_at: dayWindows[0].opens_at, closes_at: dayWindows[0].closes_at };
                                    } else {
                                        const first = dayWindows[0];
                                        const last = dayWindows[dayWindows.length - 1];
                                        byDay[value] = {
                                            opens_at: first.opens_at,
                                            closes_at: last.closes_at,
                                            break_start: first.closes_at,
                                            break_end: last.opens_at,
                                        };
                                    }
                                });
                                openEditor('availability', {
                                    byDay,
                                    slot_minutes: config.slot_minutes ?? '',
                                    concurrent_capacity: config.concurrent_capacity ?? '',
                                    min_notice_minutes: config.min_notice_minutes ?? '',
                                    max_advance_days: config.max_advance_days ?? '',
                                });
                            }}
                            onCancel={closeEditor}>
                            {editing === 'availability' ? (
                                <>
                                    {WEEKDAYS.map(([value, label]) => (
                                        <DayRow key={value} label={label} disabled={isSaving}
                                            window={draft.byDay?.[value]}
                                            onChange={(next) => setDraft((d) => ({ ...d, byDay: { ...d.byDay, [value]: next } }))} />
                                    ))}
                                    <Pair>
                                        <SelectRow bare id="s-slot" label="Appointment length" value={draft.slot_minutes} onChange={setField('slot_minutes')} disabled={isSaving}
                                            note="Each booking takes up this much of your day."
                                            options={SLOT_LENGTHS.map((m) => [m, `${m} minutes`])} />
                                        <SelectRow bare id="s-cap" label="Cars at a time" value={draft.concurrent_capacity} onChange={setField('concurrent_capacity')} disabled={isSaving}
                                            note="How many drivers can book the same slot."
                                            options={[1, 2, 3, 4, 5, 6, 8, 10].map((n) => [n, `${n} ${n === 1 ? 'car' : 'cars'}`])} />
                                    </Pair>
                                    <Pair>
                                        <SelectRow bare id="s-notice" label="How far ahead" value={draft.min_notice_minutes} onChange={setField('min_notice_minutes')} disabled={isSaving}
                                            note="The least warning a driver can give you."
                                            options={NOTICE_CHOICES} />
                                        <SelectRow bare id="s-horizon" label="How far out" value={draft.max_advance_days} onChange={setField('max_advance_days')} disabled={isSaving}
                                            note="The furthest ahead a driver can book."
                                            options={HORIZON_CHOICES.map((d) => [d, `${d} days`])} />
                                    </Pair>
                                    {errorBox}
                                </>
                            ) : (
                                <>
                                    {summariseHours(availability?.hours).map((group) => (
                                        <ReadRow pill key={group.days} label={group.days} value={group.text || 'Closed'} />
                                    ))}
                                    <ReadRow
                                        pill label="Appointment length"
                                        value={config.slot_minutes ? `${config.slot_minutes} minutes` : 'Not set'}
                                        note="Each booking takes up this much of your day." />
                                    <ReadRow
                                        pill label="Cars at a time"
                                        value={config.concurrent_capacity
                                            ? `${config.concurrent_capacity} ${config.concurrent_capacity === 1 ? 'car' : 'cars'}`
                                            : 'Not set'}
                                        note="How many drivers can book the same time slot." />
                                    <ReadRow
                                        pill label="How far ahead"
                                        value={config.min_notice_minutes === null || config.min_notice_minutes === undefined
                                            ? 'Not set'
                                            : (NOTICE_CHOICES.find(([v]) => v === config.min_notice_minutes)?.[1] || `${config.min_notice_minutes} min`)}
                                        note="The least warning a driver can give you." />
                                    <ReadRow
                                        pill label="How far out"
                                        value={config.max_advance_days ? `${config.max_advance_days} days` : 'Not set'}
                                        note="The furthest into the future a driver can book." />
                                    {hoursUnset || slotUnset ? (
                                        <p className="mt-3 rounded-xl border border-[#f7d58f] bg-[#fff8e8] p-3 text-sm font-medium leading-relaxed text-[#7a5200]">
                                            {hoursUnset
                                                ? 'You have not set your opening hours, so drivers are being offered 9:00 AM to 5:00 PM, Monday to Friday. '
                                                : ''}
                                            {slotUnset
                                                ? 'You have not set an appointment length, so each booking is taking 30 minutes. '
                                                : ''}
                                            Set your real times below so nobody books a slot you are closed for.
                                        </p>
                                    ) : null}
                                    {savedCard === 'availability' ? <p className="mt-3 text-sm font-bold text-[#087A43]">Saved.</p> : null}
                                </>
                            )}
                        </SectionCard>
                    </form>

                    {/* ── Your account ─────────────────────────────────────────── */}
                    {/* The email and phone that sign you in ARE the shop's, so they are
                        shown here but edited in Shop details — one value, one place to
                        change it, rather than two fields that quietly disagree. */}
                    <form onSubmit={submitAccount}>
                        <SectionCard icon={ICONS.user} title="Your account" description="How you sign in to this portal."
                            canEdit isEditing={editing === 'account'} isSaving={isSaving}
                            onEdit={() => openEditor('account', { owner_name: user?.name || '' })}
                            onCancel={closeEditor}>
                            {editing === 'account' ? (
                                <>
                                    <EditRow id="s-owner" label="Your name" value={draft.owner_name} onChange={setField('owner_name')} disabled={isSaving} required />
                                    {errorBox}
                                </>
                            ) : (
                                <>
                                    <ReadRow label="Your name" value={user?.name} />
                                    <ReadRow label="Sign-in email" value={user?.email} locked
                                        note={user?.email_verified
                                            ? 'This is your shop email. Change it under Shop details.'
                                            : 'This is your shop email. Not confirmed yet. Change it under Shop details to confirm.'} />
                                    <ReadRow label="Sign-in phone" value={user?.phone} locked
                                        note={user?.phone
                                            ? (user?.phone_verified
                                                ? 'This is your shop phone. Change it under Shop details.'
                                                : 'This is your shop phone. Not confirmed yet. Change it under Shop details to confirm.')
                                            : 'Not set. Add a shop phone under Shop details to sign in by phone.'} />
                                    {savedCard === 'account' ? <p className="mt-3 text-sm font-bold text-[#087A43]">Saved.</p> : null}
                                </>
                            )}
                        </SectionCard>
                    </form>
                </div>
            </div>
        </div>
    );
}
